import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, X, MessageSquare, Send, Bug, Lightbulb, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AnnouncementBanner() {
  const [show, setShow] = useState(() => {
    return sessionStorage.getItem('tm_announce_dismissed') !== 'true';
  });

  const dismiss = () => {
    setShow(false);
    sessionStorage.setItem('tm_announce_dismissed', 'true');
  };

  if (!show) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="fixed top-0 left-0 right-0 z-[60] border-b border-amber-500/20 bg-black/95 backdrop-blur-md"
      data-testid="announcement-banner"
    >
      <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Info className="h-4 w-4 text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-300/90">
            <span className="font-bold font-mono">TEST LAUNCH</span>
            <span className="text-amber-200/60 ml-2">— Early access release. Features may change. Your feedback shapes the experience!</span>
          </p>
        </div>
        <button
          onClick={dismiss}
          className="text-amber-400/50 hover:text-amber-300 transition-colors flex-shrink-0 ml-4"
          data-testid="dismiss-announcement-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}

export function FeedbackButton() {
  const [showModal, setShowModal] = useState(false);
  const [msg, setMsg] = useState('');
  const [type, setType] = useState('general');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!msg.trim()) {
      toast.error('Please enter your feedback');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/feedback`, { type, message: msg }, { withCredentials: true });
      toast.success('Feedback submitted — thank you!');
      setMsg('');
      setShowModal(false);
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 z-[55] bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 text-blue-400 px-4 py-2.5 rounded-full flex items-center gap-2 transition-all hover:scale-105 shadow-lg shadow-blue-500/10 backdrop-blur-md max-md:bottom-[220px] max-md:px-3 max-md:py-2 max-md:text-xs"
        data-testid="open-feedback-btn"
      >
        <MessageSquare className="h-4 w-4" />
        <span className="text-sm font-mono font-bold">Support</span>
      </button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-6"
            onClick={() => !sending && setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-card p-6 rounded-2xl max-w-lg w-full"
              data-testid="feedback-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-blue-400 font-mono flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Support & Feedback
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-slate-500 hover:text-slate-300"
                  data-testid="close-feedback-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-xs font-mono block mb-1.5">Category</label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="bg-slate-950/50 border-white/10 text-slate-200" data-testid="feedback-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">
                      <SelectItem value="general"><span className="flex items-center gap-2"><Mail className="h-3 w-3" /> General Feedback</span></SelectItem>
                      <SelectItem value="bug"><span className="flex items-center gap-2"><Bug className="h-3 w-3" /> Bug Report</span></SelectItem>
                      <SelectItem value="suggestion"><span className="flex items-center gap-2"><Lightbulb className="h-3 w-3" /> Suggestion</span></SelectItem>
                      <SelectItem value="feedback"><span className="flex items-center gap-2"><MessageSquare className="h-3 w-3" /> Feedback</span></SelectItem>
                      <SelectItem value="complaint"><span className="flex items-center gap-2"><X className="h-3 w-3" /> Complaint</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-slate-400 text-xs font-mono block mb-1.5">Your Message</label>
                  <Textarea
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                    placeholder="Tell us about your experience, report a bug, or suggest a feature..."
                    rows={5}
                    className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200 resize-none"
                    data-testid="feedback-message-input"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-slate-600 text-xs space-y-1">
                    <p>
                      Email:{' '}
                      <a href="mailto:TalematrixOrigional@gmail.com" className="text-blue-400 hover:text-blue-300 underline" data-testid="feedback-email-link">
                        TalematrixOrigional@gmail.com
                      </a>
                    </p>
                    <p>
                      Twitter:{' '}
                      <a href="https://x.com/TaleMatrix" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline" data-testid="feedback-twitter-link">
                        @TaleMatrix
                      </a>
                    </p>
                  </div>
                  <Button
                    onClick={submit}
                    disabled={sending || !msg.trim()}
                    className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-400/30 font-mono"
                    data-testid="submit-feedback-btn"
                  >
                    {sending ? (
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
